import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { Delete, OpenInNew, Save } from '@material-ui/icons';

import { scoreInformationActions, userActions } from '../../../../actions';
import {
  QCV_CONFIG_CODE,
  buildQcvConfigPayload,
  findQcvConfig,
  isHttpUrl,
} from '../../../../utils/qcv.appConfig';

// Cấu hình link folder Google Drive tài liệu Ban tổ chức. Link này hiện thành
// 1 nút ở màn Bình chọn (cạnh nút "Xem kết quả bình chọn"), DÙNG CHUNG cho mọi
// ứng viên. Lưu theo NĂM TỔ CHỨC ĐANG MỞ.
export default function CauHinhTaiLieuBTC() {
  const dispatch = useDispatch();

  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);

  const loading = useSelector((state) => state.scoreInformationReducer.loading);
  const openSeasonId = useSelector((state) => state.seasonReducer.openSeasonId);
  const seasonList = useSelector((state) => state.seasonReducer.seasonList);
  const scoreInformationList = useSelector(
    (state) => state.scoreInformationReducer.scoreInformationList
  );

  // Màn này có thể được mở trước khi bảng điểm của mùa đang mở được tải
  // (nhánh GTNS không tải sẵn ở AppLayout) -> tự nạp.
  useEffect(() => {
    if (openSeasonId)
      dispatch(scoreInformationActions.getScoreInformationBySeasonId(openSeasonId));
    // eslint-disable-next-line
  }, [openSeasonId]);

  const existing = useMemo(
    () => findQcvConfig(scoreInformationList, QCV_CONFIG_CODE.BTC_DRIVE_URL),
    [scoreInformationList]
  );

  const openYear = useMemo(
    () => seasonList.find((e) => e.id === openSeasonId)?.year,
    [seasonList, openSeasonId]
  );

  // Đồng bộ ô nhập với giá trị đã lưu, nhưng KHÔNG ghi đè khi đang gõ.
  useEffect(() => {
    if (!touched) setUrl(existing?.description || '');
    // eslint-disable-next-line
  }, [existing?.description]);

  const noti = (message, type) =>
    dispatch(userActions.notification({ message, type }));

  const handleSave = async () => {
    const value = url.trim();
    if (!value) {
      return noti('Chưa nhập link. Muốn bỏ nút thì bấm "Xoá link".', 'warning');
    }
    if (!isHttpUrl(value)) {
      return noti('Link phải bắt đầu bằng http:// hoặc https://', 'error');
    }
    if (!openSeasonId) {
      return noti('Chưa có năm tổ chức nào đang mở.', 'error');
    }

    const payload = buildQcvConfigPayload({
      seasonId: openSeasonId,
      code: QCV_CONFIG_CODE.BTC_DRIVE_URL,
      value,
      existing,
    });

    const saved = existing?.id
      ? await dispatch(scoreInformationActions.updateScoreInformation(payload))
      : await dispatch(scoreInformationActions.createScoreInformation(payload));

    if (saved) {
      setTouched(false);
      noti('Đã lưu link tài liệu Ban tổ chức.', 'success');
    }
  };

  const handleDelete = async () => {
    if (!existing?.id) return;
    const ok = await dispatch(
      scoreInformationActions.deleteScoreInformation(existing.id)
    );
    if (ok) {
      setTouched(false);
      setUrl('');
      noti('Đã xoá link. Nút ở màn Bình chọn sẽ không hiện nữa.', 'success');
    }
  };

  return (
    <Grid container spacing={1} style={{ margin: 0, width: '100%' }}>
      <Grid item xs={12}>
        <Typography variant='h5'>
          <b>Tài liệu Ban tổ chức (màn Bình chọn)</b>
        </Typography>
        <Typography variant='body2' style={{ color: '#666', marginTop: 4 }}>
          Link folder Google Drive dùng chung cho tất cả ứng viên. Hội đồng bình
          chọn sẽ thấy một nút mở link này ở màn Bình chọn, cạnh nút “Xem kết quả
          bình chọn”. Bỏ trống/xoá thì nút không hiện.
          {openYear ? ` Đang cấu hình cho năm ${openYear}.` : ''}
        </Typography>
      </Grid>

      <Grid item xs={12}><Divider style={{ margin: '8px 0' }} /></Grid>

      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          label='Link folder Google Drive'
          placeholder='https://drive.google.com/drive/folders/...'
          value={url}
          onChange={(e) => {
            setTouched(true);
            setUrl(e.target.value);
          }}
          error={!!url.trim() && !isHttpUrl(url)}
          helperText={
            !!url.trim() && !isHttpUrl(url)
              ? 'Link phải bắt đầu bằng http:// hoặc https://'
              : ' '
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box display='flex' flexWrap='wrap' style={{ gap: 8 }}>
          <Button
            color='primary'
            variant='contained'
            startIcon={<Save />}
            disabled={loading}
            onClick={handleSave}
          >
            Lưu
          </Button>
          <Button
            variant='outlined'
            startIcon={<OpenInNew />}
            disabled={!isHttpUrl(url)}
            onClick={() => window.open(url.trim(), '_blank', 'noopener,noreferrer')}
          >
            Thử mở
          </Button>
          <Button
            color='secondary'
            variant='outlined'
            startIcon={<Delete />}
            disabled={loading || !existing?.id}
            onClick={handleDelete}
          >
            Xoá link
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}
